// ExportSettingsDrawer — the config surface for company-wide SQL exports. One
// component, two call sites:
//   · DataSetupPage's "SQL settings" — the four standing exports (users, tasks,
//     attendance, company submissions)
//   · FormStructureTab's "Form structure" — the fifth managed type, on its own
//
// They are one component because their bodies are identical, and the confirm
// and toast copy is already written generically: every string interpolates
// `{{name}}` (the section's own label) and `{{table}}`, so none of it names SQL
// or the company. The section list is a prop rather than an import for the same
// reason — see companyExportSections, which keeps the two lists apart so
// form_structure_export can never drift into the four.
//
// Each section has exactly two states, decided by whether exports/list returned
// an entry of its type:
//   · none  → the DERIVED table name + Save
//   · one   → its OWN tableName, when it last ran, Re-import and Delete
//
// Table names are never typed — the backend names these by convention and
// companyExportSections reproduces it — so the field is a locked display and no
// identifier validation happens anywhere in here.
//
// All three actions confirm. Save creates a warehouse table and exports into
// it; Re-import overwrites every row already exported; Delete drops that data.
// The per-form connect/disconnect flow already confirms both its directions, so
// one module doesn't hold two standards for the same consequence.
//
// Re-import keys on TYPE (no export id needed) and is assumed asynchronous, so
// its toast says *started* rather than claiming the data has moved.
//
// Sections start closed and only one opens at a time — the
// FormBuilderAdvancedOptions idiom, which swaps the chevron rather than
// rotating it. A drawer given a SINGLE section opens it instead: there is
// nothing to choose between, so "all closed" would just be an extra click.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, ConfirmModal, Drawer, Icon, Input } from '@/components/common';
import {
  useAddCompanyExportMutation,
  useGetCompanyExportsQuery,
  useReimportCompanyExportMutation,
  useRemoveExportMutation,
  type CompanyExport,
  type CompanyManagedExportType,
} from '@/api/endpoints/dataSetupApi';
import type { ApiErrorShape } from '@/api/types';
import { useAppSelector } from '@/store/hooks';
import { selectCompanyDetail } from '@/redux/auth/selectors';
import i18n from '@/i18n/config';
import { tableNameFor, type CompanyExportSection } from './companyExportSections';

interface ExportSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Sections to render, in order. A one-element list opens on mount. */
  sections: CompanyExportSection[];
  title: string;
  subtitle: string;
  /** Kebab-case name for the header tile. Omitted = no tile. */
  icon?: string;
}

/** Which action a confirm is for. Delete and Re-import both need the existing
 *  export; Save is the only one that works without one. */
type PendingAction = 'save' | 'reimport' | 'delete';

interface Pending {
  section: CompanyExportSection;
  action: PendingAction;
  existing: CompanyExport | null;
}

/** "14 Aug 2026, 16:30" — the treatment the table's timestamps get. */
const fmtDateTime = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
};

const ExportSettingsDrawer = ({
  open,
  onClose,
  sections,
  title,
  subtitle,
  icon,
}: ExportSettingsDrawerProps) => {
  const { t } = useTranslation();
  const companyCode = useAppSelector(selectCompanyDetail)?.companyCode;

  // A lone section has nothing to choose between, so it starts open; several
  // start closed and compete. Read once — `sections` is a module constant at
  // both call sites.
  const [openSection, setOpenSection] = useState<CompanyManagedExportType | null>(() =>
    sections.length === 1 ? sections[0].type : null,
  );
  const [pending, setPending] = useState<Pending | null>(null);

  // Skipped while closed, so opening the drawer is what fetches.
  const { data: exports = [], isLoading } = useGetCompanyExportsQuery(undefined, { skip: !open });
  const [addCompanyExport, { isLoading: saving }] = useAddCompanyExportMutation();
  const [reimportCompanyExport, { isLoading: reimporting }] = useReimportCompanyExportMutation();
  const [removeExport, { isLoading: deleting }] = useRemoveExportMutation();

  // The response is every export the company holds; a drawer renders only its
  // own sections, so the rest are simply never looked up.
  const byType = useMemo(() => new Map(exports.map((e) => [e.type, e])), [exports]);

  const runConfirm = async () => {
    if (!pending) return;
    const { section, action, existing } = pending;
    const name = t(section.labelKey);
    try {
      if (action === 'save') {
        await addCompanyExport({
          type: section.type,
          tableName: tableNameFor(section, companyCode),
        }).unwrap();
        toast.success(t('data_setup_sql_saved', { name }));
      } else if (action === 'reimport') {
        await reimportCompanyExport(section.type).unwrap();
        toast.success(t('data_setup_sql_reimport_started', { name }));
      } else if (existing) {
        await removeExport(existing.id).unwrap();
        toast.success(t('data_setup_sql_deleted', { name }));
      }
      setPending(null);
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
      setPending(null);
    }
  };

  const confirmCopy = (p: Pending) => {
    const name = t(p.section.labelKey);
    const table = p.existing?.tableName ?? tableNameFor(p.section, companyCode);
    if (p.action === 'save')
      return {
        tone: 'brand' as const,
        icon: 'database',
        title: t('data_setup_sql_save_title', { name }),
        description: t('data_setup_sql_save_body', { table }),
        confirmLabel: t('button_save'),
        confirmVariant: 'primary' as const,
      };
    if (p.action === 'reimport')
      return {
        tone: 'brand' as const,
        icon: 'refresh-ccw',
        title: t('data_setup_sql_reimport_title', { name }),
        description: t('data_setup_sql_reimport_body', { table }),
        confirmLabel: t('data_setup_sql_reimport'),
        confirmVariant: 'primary' as const,
      };
    return {
      tone: 'danger' as const,
      icon: 'trash-2',
      title: t('data_setup_sql_delete_title', { name }),
      description: t('data_setup_sql_delete_body', { table }),
      confirmLabel: t('data_setup_sql_delete'),
      confirmVariant: 'danger' as const,
    };
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        className='export-settings'
        icon={icon}
        title={title}
        subtitle={subtitle}
      >
        <div className='export-settings__sections'>
          {sections.map((section) => {
            const existing = byType.get(section.type) ?? null;
            const isOpen = openSection === section.type;
            // A saved export carries its own name; an unsaved one shows what it
            // WOULD be called.
            const tableName = existing?.tableName ?? tableNameFor(section, companyCode);

            return (
              <div key={section.type} className='export-settings__section'>
                <button
                  type='button'
                  className='export-settings__head'
                  aria-expanded={isOpen}
                  onClick={() =>
                    setOpenSection((prev) => (prev === section.type ? null : section.type))
                  }
                >
                  <span className='export-settings__tile' aria-hidden='true'>
                    <Icon name={section.icon} size={18} />
                  </span>
                  <span className='export-settings__label'>{t(section.labelKey)}</span>
                  <Icon name={isOpen ? 'chevron-up' : 'chevron-down'} size={16} />
                </button>

                {isOpen && (
                  <div className='export-settings__body'>
                    <span className='export-settings__field-label'>
                      {t('data_setup_sql_table_name')}
                    </span>

                    {tableName ? (
                      <Input
                        className='export-settings__table-name'
                        size='sm'
                        // Set by the system — shown, never typed.
                        disabled
                        iconEnd='lock'
                        dir='ltr'
                        value={tableName}
                        readOnly
                        aria-label={t('data_setup_sql_table_name')}
                      />
                    ) : (
                      // No company code means no derivable name, so there is
                      // nothing to save — say so rather than render a broken one.
                      <p className='export-settings__unavailable'>
                        {t('data_setup_sql_no_company_code')}
                      </p>
                    )}

                    <span className='export-settings__meta'>
                      {existing?.updatedAt
                        ? t('data_setup_sql_updated', { at: fmtDateTime(existing.updatedAt) })
                        : t('data_setup_sql_never_updated')}
                    </span>

                    {tableName && (
                      <div className='export-settings__actions'>
                        {existing ? (
                          <>
                            <Button
                              variant='secondary'
                              iconStart='refresh-ccw'
                              onClick={() => setPending({ section, action: 'reimport', existing })}
                            >
                              {t('data_setup_sql_reimport')}
                            </Button>
                            <Button
                              variant='ghost'
                              iconStart='trash-2'
                              onClick={() => setPending({ section, action: 'delete', existing })}
                            >
                              {t('data_setup_sql_delete')}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant='secondary'
                            iconStart='save'
                            disabled={isLoading}
                            onClick={() => setPending({ section, action: 'save', existing: null })}
                          >
                            {t('button_save')}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Drawer>

      {pending && (
        <ConfirmModal
          open
          onClose={() => setPending(null)}
          onConfirm={runConfirm}
          loading={saving || reimporting || deleting}
          {...confirmCopy(pending)}
        />
      )}
    </>
  );
};

export default ExportSettingsDrawer;
